import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api from '../Services/api';

interface ContactSubmission {
  _id: string;
  name: string;
  email: string;
  category: string;
  message: string;
  resolved: boolean;
  replied: boolean;
  dateCreated: string;
}

export default function SubmissionDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Try to get from state, otherwise null
  const initialSubmission = location.state?.submission as ContactSubmission | null;

  const [submission, setSubmission] = useState<ContactSubmission | null>(initialSubmission);
  const [loading, setLoading] = useState(!initialSubmission);

  useEffect(() => {
    // If we don't have submission data, fetch it by ID
    if (!submission && id) {
      const token = localStorage.getItem("token");
      setLoading(true);
      api.get(`/contact/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => setSubmission(res.data))
        .catch(() => setSubmission(null))
        .finally(() => setLoading(false));
    }
  }, [id, submission]);

  if (loading) {
    return <div className="p-8 text-gray-700 dark:text-gray-100">Loading...</div>;
  }

  if (!submission) {
    return (
      <div className="p-8 text-gray-700 dark:text-gray-100">
        <p>Submission details not available. Please go back.</p>
        <button
          onClick={() => navigate("/admin")}
          className="mt-4 px-4 py-2 bg-gray-500 text-white rounded"
        >
          Back
        </button>
      </div>
    );
  }

  const token = localStorage.getItem("token");

  // Updates a specific field (resolved or replied) in the submission.
  const updateSubmission = async (
    field: "resolved" | "replied",
    value: boolean
  ) => {
    try {
      await api.patch(
        `/contact/${submission._id}`,
        { [field]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubmission({ ...submission, [field]: value });
    } catch (error) {
      console.error("Error updating submission:", error);
    }
  };

  // Format date as "Feb 19, 2025, 4:38 PM"
  const formattedDate = new Date(submission.dateCreated).toLocaleString(
    "en-US",
    {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    }
  );

  return (
    <div className="p-4 md:p-8 max-w-[600px] md:max-w-xl w-full text-gray-700 dark:text-gray-100">
      <div className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
        <h1 className="text-3xl font-bold mb-6">Submission Detail</h1>
        <form className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Name
            </label>
            <input
              type="text"
              value={submission.name}
              disabled
              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 shadow-sm text-gray-900 dark:text-gray-100 px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Email
            </label>
            <input
              type="email"
              value={submission.email}
              disabled
              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 shadow-sm text-gray-900 dark:text-gray-100 px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Category
            </label>
            <input
              type="text"
              value={submission.category}
              disabled
              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 shadow-sm text-gray-900 dark:text-gray-100 px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Message
            </label>
            <textarea
              value={submission.message}
              disabled
              rows={4}
              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 shadow-sm text-gray-900 dark:text-gray-100 px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Date Created
            </label>
            <input
              type="text"
              value={formattedDate}
              disabled
              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 shadow-sm text-gray-900 dark:text-gray-100 px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Resolved
            </label>
            <input
              type="text"
              value={submission.resolved ? "Yes" : "No"}
              disabled
              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 shadow-sm text-gray-900 dark:text-gray-100 px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Replied
            </label>
            <input
              type="text"
              value={submission.replied ? "Yes" : "No"}
              disabled
              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 shadow-sm text-gray-900 dark:text-gray-100 px-3 py-2"
            />
          </div>
        </form>
        <div className="mt-6 flex flex-wrap gap-4">
          <button
            onClick={() => updateSubmission("resolved", !submission.resolved)}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded transition"
          >
            Mark as {submission.resolved ? "Unresolved" : "Resolved"}
          </button>
          <button
            onClick={() => updateSubmission("replied", !submission.replied)}
            className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded transition"
          >
            Mark as {submission.replied ? "Not Replied" : "Replied"}
          </button>
          <button
            onClick={() => navigate("/admin")}
            className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded transition"
          >
            Back
          </button>
        </div>
      </div>
    </div>
  );
}